import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthProvider, useTranslate } from "ra-core";
import { Layout } from "@/components/supabase/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Authorization UI for OAuth Consent Page
 *
 * When third-party apps initiate OAuth, users will be redirected to this page
 * to approve or deny the authorization request.
 *
 * Anonymous users will be redirected to the login page first.
 *
 * Inspired from https://supabase.com/docs/guides/auth/oauth-server/getting-started?queryGroups=oauth-setup&oauth-setup=dashboard#example-authorization-ui
 */
/**
 * Supabase does not return the shape this page was written against: it expects
 * a nested `client` object and crashed on `authDetails.client.name` when that
 * was undefined, taking the whole consent screen down with it.
 *
 * These read either shape and fall back to a plain label. A consent screen that
 * cannot name the application is worth showing -- the redirect_uri below it
 * still tells the user where they are about to send access. A consent screen
 * that throws is not.
 */
const clientNameOf = (details: OAuthAuthorizationDetails | null) =>
  details?.client?.name ??
  (details as unknown as { client_name?: string } | null)?.client_name ??
  "An application";

/**
 * The redirect to hand back to after a decision.
 *
 * Read defensively for the same reason as the client fields: this page assumed
 * `redirect_url`, and if Supabase names it otherwise the assignment silently
 * navigates nowhere. The user then sits on an apparently working page, presses
 * the button again, and the second request is refused with "authorization
 * request is no longer pending" -- an error about the retry, not about the
 * cause.
 */
const redirectUrlOf = (data: unknown): string | null => {
  const d = data as Record<string, unknown> | null | undefined;
  const candidate = d?.redirect_url ?? d?.redirect_uri ?? d?.url;
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : null;
};

const clientUriOf = (details: OAuthAuthorizationDetails | null) =>
  details?.client?.uri ??
  (details as unknown as { client_uri?: string } | null)?.client_uri ??
  null;

/** Scope may arrive as a space-delimited string or as an array. */
const scopesOf = (details: OAuthAuthorizationDetails | null): string[] => {
  const scope = details?.scope as unknown;
  if (Array.isArray(scope)) return scope.map(String).filter(Boolean);
  if (typeof scope === "string") return scope.split(" ").filter(Boolean);
  return [];
};

export function OAuthConsentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Supabase redirects to https://host/oauth/consent?authorization_id=...,
  // putting the query BEFORE the hash. This app runs on a hash router, where
  // useSearchParams only reads the query INSIDE the hash, so on the real
  // redirect it finds nothing. Fall back to the document's own query string.
  const authorizationId =
    searchParams.get("authorization_id") ??
    new URLSearchParams(window.location.search).get("authorization_id");
  const authProvider = useAuthProvider();
  const translate = useTranslate();

  const [authDetails, setAuthDetails] =
    useState<OAuthAuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    async function loadAuthDetails() {
      if (!authorizationId) {
        setError("Missing authorization_id");
        setLoading(false);
        return;
      }
      if (!authProvider) {
        setError("Auth provider not available");
        setLoading(false);
        return;
      }

      // Check if user is authenticated
      try {
        await authProvider.checkAuth({});
      } catch {
        // Encoded, because the target itself carries a query. Unencoded, the
        // second "?" ends the redirect value and authorization_id is parsed as
        // a parameter of /login instead -- so signing in landed back here with
        // nothing to consent to. Anyone not already signed in hit this, which
        // during an OAuth flow is most people.
        const target = `/oauth/consent?authorization_id=${encodeURIComponent(
          authorizationId,
        )}`;
        navigate(`/login?redirect=${encodeURIComponent(target)}`);
        return;
      }

      // Get authorization details using the authorization_id
      const { data, error } =
        await authProvider.getAuthorizationDetails(authorizationId);

      if (error) {
        setError(error.message);
      } else {
        const details = data as OAuthAuthorizationDetails;
        if (!details?.client?.name) {
          console.warn(
            "[oauth-consent] unexpected authorization details shape:",
            data,
          );
        }
        setAuthDetails(details);
      }

      setLoading(false);
    }

    loadAuthDetails();
  }, [authProvider, authorizationId, navigate]);

  async function handleApprove() {
    if (!authorizationId || !authProvider) return;

    setSubmitting(true);
    const { data, error } =
      await authProvider.approveAuthorization(authorizationId);

    if (error) {
      setError(
        /no longer pending|expired/i.test(error.message ?? "")
          ? "This authorization link has already been used or has expired. " +
              "Start the connection again from the app you were connecting."
          : error.message,
      );
      setSubmitting(false);
    } else {
      const redirectUrl = redirectUrlOf(data);
      if (!redirectUrl) {
         
        console.error(
          "[oauth-consent] no redirect url in approve response:",
          data,
        );
        setError(
          "Access was approved, but this app did not receive a return address " +
            "to send you back to. Start the connection again from the app you " +
            "were connecting.",
        );
        setSubmitting(false);
        return;
      }
      setApproved(true);
      window.location.href = redirectUrl;
    }
  }

  async function handleDeny() {
    if (!authorizationId || !authProvider) return;

    setSubmitting(true);
    const { data, error } =
      await authProvider.denyAuthorization(authorizationId);
    if (error) {
      setError(error.message);
      setSubmitting(false);
    } else {
      const redirectUrl = redirectUrlOf(data);
      if (!redirectUrl) {
        setError("Access was denied. You can close this page.");
        setSubmitting(false);
        return;
      }
      window.location.href = redirectUrl;
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col space-y-2 text-center">
          <p className="text-muted-foreground">
            {translate("ra.message.loading", { _: "Loading..." })}
          </p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("ra.message.error", { _: "Error" })}
          </h1>
          <p className="text-destructive">{error}</p>
        </div>
      </Layout>
    );
  }

  if (!authDetails) {
    return (
      <Layout>
        <div className="flex flex-col space-y-2 text-center">
          <p className="text-muted-foreground">
            {translate("ra-supabase.oauth.no_request", {
              _: "No authorization request found",
            })}
          </p>
        </div>
      </Layout>
    );
  }

  if (approved) {
    return (
      <Layout>
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("ra-supabase.oauth.approved", {
              _: "Authorization Approved",
            })}
          </h1>
          <p className="text-muted-foreground">
            {translate("ra-supabase.oauth.close_tab", {
              _: "You can now close this tab.",
            })}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.oauth.authorize", {
            _: "Authorize Application",
          })}
        </h1>
        <p className="text-muted-foreground">
          {translate("ra-supabase.oauth.authorize_details", {
            _: "This application wants to access your account",
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{clientNameOf(authDetails)}</CardTitle>
          <CardDescription>
            {clientUriOf(authDetails) ?? authDetails.redirect_uri}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {scopesOf(authDetails).length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {translate("ra-supabase.oauth.permissions", {
                  _: "Requested permissions",
                })}
              </p>
              <ul className="list-disc list-inside space-y-1">
                {scopesOf(authDetails).map((scopeItem) => (
                  <li key={scopeItem} className="text-sm">
                    {scopeItem}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleDeny}
            disabled={submitting}
            className="flex-1"
          >
            {translate("ra.action.cancel", { _: "Deny" })}
          </Button>
          <Button
            onClick={handleApprove}
            disabled={submitting}
            className="flex-1"
          >
            {translate("ra.action.confirm", { _: "Approve" })}
          </Button>
        </CardFooter>
      </Card>
    </Layout>
  );
}

OAuthConsentPage.path = "/oauth/consent";

/**
 * copied from @supabase/auth-js/src/lib/types.ts
 * to avoid adding a hard import to a Supabase package
 * because this page can also be used with FakeRest
 */
type OAuthAuthorizationDetails = {
  /** The authorization ID */
  authorization_id: string;
  /** Redirect URL - present if user already consented (can be used to trigger immediate redirect) */
  redirect_uri?: string;
  /** User object associated with the authorization */
  /** OAuth client requesting authorization */
  client: {
    /** Unique identifier for the OAuth client (UUID) */
    id: string;
    /** Human-readable name of the OAuth client */
    name: string;
    /** URI of the OAuth client's website */
    uri: string;
    /** URI of the OAuth client's logo */
    logo_uri: string;
  };
  user: {
    /** User ID (UUID) */
    id: string;
    /** User email */
    email: string;
  };
  /** Space-separated list of requested scopes */
  scope: string;
};
